/**
 * Redux slice for company/housing cooperative data.
 *
 * @remarks
 * Companies are fetched via the aggregation proxy (`POST /serviceman/companies`
 * with no request body) which fans-out to all of the user's environments.
 * Individual company details require an `env` parameter (from `__source_env`)
 * to route to the correct single environment.
 */

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import type { RootState } from '../../store'
import api from '../../api/axios'
import { Company, CompanyDetails } from '../../types/company'

/** Redux state for the company feature slice. */
interface CompanyState {
  companies: Company[]
  master: any[] // Master list from backend
  selectedCompany: CompanyDetails | null
  loading: boolean
  error: string | null
  total: number
}

const initialState: CompanyState = {
  companies: [],
  master: [],
  selectedCompany: null,
  loading: false,
  error: null,
  total: 0,
}

// Async Thunks

/**
 * Fetch all companies for the current serviceman.
 *
 * @remarks
 * Backend: `POST /serviceman/companies` with NO body.
 * Uses the aggregation proxy — fans out to all user environments.
 * Response: `{ result: Company[], master: Master[] }`.
 * The `master` array indicates which companies the user has admin rights for.
 *
 * @returns `{ companies, master, total }` on success
 * @throws Rejects with Finnish error string on failure
 */
export const fetchCompanies = createAsyncThunk('company/fetchAll', async (_, { rejectWithValue }) => {
  try {
    // Backend expects POST to /serviceman/companies with NO request body
    const response = await api({
      method: 'POST',
      url: '/serviceman/companies',
    } as any)
    // Backend returns: { result: Company[], master: Master[] }
    return {
      companies: response.data.result || [],
      master: response.data.master || [],
      total: response.data.result?.length || 0,
    }
  } catch (error) {
    return rejectWithValue('Yritysten haku epäonnistui')
  }
})

/**
 * Fetch details for a single company.
 *
 * @remarks
 * Backend: `GET /serviceman/company/:companyId`.
 * Requires the company's `__source_env` as the `env` parameter
 * so the proxy routes to the correct single environment.
 *
 * @param companyId - The company ID to fetch
 * @param env - The `__source_env` value identifying the source tenant environment
 * @throws Rejects with Finnish error string on failure
 */
export const fetchCompanyDetails = createAsyncThunk(
  'company/fetchDetails',
  async ({ companyId, env }: { companyId: string; env: string | number }, { rejectWithValue }) => {
    try {
      const response = await api({
        method: 'GET',
        url: `/serviceman/company/${companyId}`,
        env: env, // Required: use company's __source_env
      } as any)
      return response.data.result
    } catch (error) {
      return rejectWithValue('Yrityksen tietojen haku epäonnistui')
    }
  }
)

// Slice
const companySlice = createSlice({
  name: 'company',
  initialState,
  reducers: {
    setSelectedCompany: (state, action: PayloadAction<CompanyDetails | null>) => {
      state.selectedCompany = action.payload
    },
    clearError: state => {
      state.error = null
    },
  },
  extraReducers: builder => {
    // Fetch all
    builder
      .addCase(fetchCompanies.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchCompanies.fulfilled, (state, action) => {
        state.loading = false
        state.companies = action.payload.companies
        state.master = action.payload.master
        state.total = action.payload.total
      })
      .addCase(fetchCompanies.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })

    // Fetch details
    builder
      .addCase(fetchCompanyDetails.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchCompanyDetails.fulfilled, (state, action) => {
        state.loading = false
        state.selectedCompany = action.payload
      })
      .addCase(fetchCompanyDetails.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })
  },
})

// Actions
export const { setSelectedCompany, clearError } = companySlice.actions

// Selectors
/** Select the full company sub-state. */
export const selectCompany = (state: RootState) => state.company
/** Select the array of all companies. */
export const selectCompanies = (state: RootState) => state.company.companies
/** Select the currently viewed company details (or `null`). */
export const selectSelectedCompany = (state: RootState) => state.company.selectedCompany
/** Select the loading flag for company operations. */
export const selectCompanyLoading = (state: RootState) => state.company.loading
/** Select the current error message (or `null`). */
export const selectCompanyError = (state: RootState) => state.company.error

// Reducer
export default companySlice.reducer
